import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum OrderEditKind {
  ADDRESS = "address",
  CANCEL = "cancel",
  VARIANT_CHANGE = "variant_change",
  ADD_PRODUCT = "add_product",
  REMOVE_PRODUCT = "remove_product",
  DISCOUNT = "discount",
  ADD_QUANTITY = "add_quantity",
  REMOVE_QUANTITY = "remove_quantity",
  CONTACT_INFO = "contact_info",
}

export enum OrderEditSource {
  "SUPPORT_AI" = "support_ai",
  "SUPPORT_SELF_SERVICE" = "support_self_service",
  "CUSTOMER_PORTAL_SELF_SERVICE" = "customer_portal_self_service",
  "EMAIL_ONE_CLICK_UPSELL" = "email_one_click_upsell",
}

export enum LineItemDiffChangeType {
  ADDED = "added",
  REMOVED = "removed",
  QUANTITY_ADDED = "quantity_added",
  QUANTITY_REMOVED = "quantity_removed",
}

const LineItemDiffSchema = z.object({
  lineItemId: z.string().meta({ privacy: privacy.nonPii }),
  variantId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  productTitle: z.string().nullish().meta({ privacy: privacy.nonPii }),
  variantTitle: z.string().nullish().meta({ privacy: privacy.nonPii }),
  sku: z.string().nullish().meta({ privacy: privacy.nonPii }),
  quantityBefore: z.number().meta({ privacy: privacy.nonPii }),
  quantityAfter: z.number().meta({ privacy: privacy.nonPii }),
  changeType: createEnum(LineItemDiffChangeType, {
    enumName: "LineItemDiffChangeType",
  }).meta({ privacy: privacy.nonPii }),
});

export const OrderEditSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  team: zExt.objectId().meta({ privacy: privacy.nonPii }),
  kind: createEnum(OrderEditKind, { enumName: "OrderEditKind" }).meta({
    privacy: privacy.nonPii,
  }),
  source: createEnum(OrderEditSource, { enumName: "OrderEditSource" }).meta({
    privacy: privacy.nonPii,
  }),
  redoOrderId: zExt.objectId().nullish().meta({ privacy: privacy.nonPii }),
  customerName: z.string().meta({ privacy: privacy.person_name.customer }),
  orderNumber: z.string().meta({ privacy: privacy.nonPii }),
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  orderCreatedAt: z.date().meta({ privacy: privacy.nonPii }),
  totalPriceBefore: z.string().nullish().meta({ privacy: privacy.nonPii }),
  totalPriceAfter: z.string().nullish().meta({ privacy: privacy.nonPii }),
  /** Shop currency of the price fields. Absent on records written before both sides were normalized to shop money. */
  currency: z.string().nullish().meta({ privacy: privacy.nonPii }),
  lineItemDiff: z
    .array(LineItemDiffSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
});

export type OrderEdit = z.infer<typeof OrderEditSchema>;
export type LineItemDiff = z.infer<typeof LineItemDiffSchema>;
